import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import React, { useEffect, useMemo } from 'react';
import STRING_KEYS from '../../../../language/keys';
import { PageTitleBold } from '../create-position-container/create-position-container.styled';
import { CreatePositionContainer } from '../create-position-container/create-position-container.component';
import CreatePositionRequirementsComponent
  from '../create-position-requirements/create-position-requirements.component';
import { useGetEducation } from '../../hooks/create-position-education-react-query.hooks';
import { useCreatePositionEducationSelector } from '../../../../store/slices/create-position-state/selectors/create-position-education.selector';
import { JobRequirementOptionTypeEnum } from '../../enums/create-position-experience-level.enums';
import { EducationType } from '../../enums/create-position-education.enums';
import { useGetStepInfoComponent, usePositionEnableContinueButton } from '../../hooks/create-position-actions.hooks';
import { setDegrees, setSuggestedFieldOfStudy, setUserInputFieldOfStudy } from '../../../../store/slices/create-position-state/create-position-state.toolkit-slice';

export const CreatePositionEducationStep = ({
  setContinueButtonEnable,
  setContinueButtonDisable,
}: CreatePositionEducationProps): JSX.Element => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const InfoComponent = useGetStepInfoComponent();
  const { isLoading: isSuggestionsEducationLoading, data: educationOptions } = useGetEducation();
  const { degrees = [], suggestedFieldsOfStudy = [], userInputFieldsOfStudy = [] } = useCreatePositionEducationSelector();
  const setSuggestedFieldsOfStudy = (fieldsOfStudy: PositionJobRequirementState[]) => {
    dispatch(setSuggestedFieldOfStudy(fieldsOfStudy));
  };

  const setDegreesHandler = (degree: PositionJobRequirementState[]) => {
    dispatch(setDegrees(degree));
  };

  const setUserInputFieldsOfStudy = (fieldsOfStudy: PositionJobRequirementState[]) => {
    dispatch(setUserInputFieldOfStudy(fieldsOfStudy));
  };

  const suggested = useMemo<PositionJobRequirementState[]>(() => ([
    ...suggestedFieldsOfStudy,
    ...degrees,
  ]), [suggestedFieldsOfStudy, degrees]);

  usePositionEnableContinueButton(
    setContinueButtonEnable,
    setContinueButtonDisable,
    suggested,
    userInputFieldsOfStudy,
  );

  useEffect(() => {
    if (!degrees?.length) {
      const newDegrees = educationOptions?.degrees.map((educationDegree) => ({
        id: `${educationDegree}`,
        title: `🎓 ${educationDegree.charAt(0).toUpperCase() + educationDegree.slice(1).toLowerCase()}`,
        type: EducationType.Degree,
        selected: false,
      }));

      setDegreesHandler(newDegrees?.sort((a, b) => {
        if (a.id < b.id) { return -1; }
        if (a.id > b.id) { return 1; }

        return 0;
      }) as PositionJobRequirementState[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationOptions?.degrees]);

  useEffect(() => {
    const newFieldsOfStudy = educationOptions?.fieldOfStudies.map((fieldOfStudies) => ({
      id: `${fieldOfStudies}`,
      title: `📚 ${fieldOfStudies}`,
      type: EducationType.FieldOfStudy,
      selected: false,
    }));

    setSuggestedFieldsOfStudy(newFieldsOfStudy as PositionJobRequirementState[]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationOptions?.fieldOfStudies]);

  const setSuggestionsJobRequirementsHandler = (values: PositionJobRequirementState[]) => {
    const { newDegrees, newFieldsOfStudy } = values.reduce((acc, jobRequirement) => {
      if (jobRequirement.type === EducationType.Degree) {
        acc.newDegrees.push(jobRequirement);
      }

      if (jobRequirement.type === EducationType.FieldOfStudy) {
        acc.newFieldsOfStudy.push(jobRequirement);
      }

      return acc;
    }, {
      newDegrees: [] as PositionJobRequirementState[],
      newFieldsOfStudy: [] as PositionJobRequirementState[],
    });

    setDegreesHandler(newDegrees);
    setSuggestedFieldsOfStudy(newFieldsOfStudy);
  };

  const getPageTitle = () => (
    <Trans
      i18nKey={ STRING_KEYS.ONBOARDING.ONBOARDING_EDUCATION_PAGE_TITLE }
    >
      <PageTitleBold />
    </Trans>
  );

  return (
    <>
      <CreatePositionContainer
        title={ getPageTitle() }
        info={ InfoComponent }
      >
        <CreatePositionRequirementsComponent
          type={ JobRequirementOptionTypeEnum.FieldOfStudy }
          placeholder={ translate(STRING_KEYS.ONBOARDING.ONBOARDING_CUSTOM_EDUCATION_PLACEHOLDER) }
          isSuggestionsJobRequirementsLoading={ isSuggestionsEducationLoading }
          suggestionsJobRequirements={ [...degrees, ...suggestedFieldsOfStudy] }
          setSuggestionsJobRequirements={ setSuggestionsJobRequirementsHandler }
          customJobRequirements={ userInputFieldsOfStudy }
          setCustomJobRequirements={ setUserInputFieldsOfStudy }
        />
      </CreatePositionContainer>
    </>
  );
};
